import { useState, useEffect } from "react";
// material
import {
  Box,
  Card,
  Typography,
  TableCell,
  TableBody,
  TableContainer,
  Table,
  TableRow,
} from "@mui/material";
// components
import { Log, sendDataWithPost } from "../../services/services";

import ListHead from "../../components/table/ListHead";
import Scrollbar from "../../components/table/Scrollbar";
import { applySortFilter } from "../../utils/tableFunctions";
import { StyledTableRow } from "../../components/TableRow";
import { isEmpty } from "lodash";

import FormSearch from "../../components/FormSearch";
import moment from "moment";
import CircularLoader from "../../components/circularLoader";
import { CustomTableCell } from "../../components/TableCell";
import Pagination from "../../components/Pagination";
import Label from "../../components/Label";
import { acquirerList } from "../../utils/constants";

// ----------------------------------------------------------------------

const defaultValue = {
  batch_date: null,
  is_in_settlement: false,
  start_datetime: moment().subtract(6, 'days'),
  end_datetime: moment()
};

const TABLE_HEAD = [
  { id: "transaction_id", label: "Transaction ID" },
  { id: "transaction_datetime", label: "Transaction Time" },
  { id: "terminal_id", label: "Terminal ID" },
  { id: "merchant_id", label: "Merchant ID" },
  { id: "merchant_name", label: "Merchant Name" },
  { id: "base_amount", label: "Amount" },
  { id: "base_currency", label: "Currency" },
  { id: "payment_brand", label: "Payment Brand" },
  { id: "acquirer", label: "Acquirer" },
  { id: "batch_date", label: "Batch Date" },
  { id: "is_in_settlement", label: "Settlement" },
];

export default function Reconciled() {
  const [loaded, setLoaded] = useState(false);
  const [tableData, setTableData] = useState({
    results: [],
  });
  const [filter, setFilter] = useState({});

  // ------------------ filterStuffies --------------------//
  const [pagination, setPagination] = useState({ page: 1, page_size: 50 });

  //Dialogs
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    queryList({
      page: newPage + 1,
      page_size: pagination.page_size,
    }, filter);
  };

  const handleChangeRowsPerPage = (event) => {
    queryList({
      page: 1,
      page_size: event.target.value,
    }, filter);
  };

  const queryList = (params, filter) => {
    loaded && setLoaded(false);
    queryPage(params, filter);
  };

  const queryPage = (params, filter) => {
    const queryParams = new URLSearchParams(params || pagination).toString();
    const url = `/payout/reconciliation/filter_reconciled/?${queryParams}`;
    sendDataWithPost(url, filter, setTableData, setLoaded, () => {
      params && setPagination(params);
    });
  };

  const onFilterSubmit = (fParams) => {
    const { batch_date, start_datetime, end_datetime, ...rest } = fParams;
    const newPrams = {
      ...rest,
      batch_date:
        batch_date && moment(batch_date).isValid()
          ? batch_date.format("YYYY-MM-DD")
          : null,
      start_transaction_date:
        start_datetime && moment(start_datetime).isValid()
          ? start_datetime.format("YYYY-MM-DD")
          : null,
      end_transaction_date:
        end_datetime && moment(end_datetime).isValid()
          ? end_datetime.format("YYYY-MM-DD")
          : null,
      acquirer: fParams.acquirer?.id,
      transaction_id: fParams.transaction_id?.trim(),
      terminal_id: fParams.terminal_id?.trim(),
      merchant_name: fParams.merchant_name?.trim(),
    };

    if (newPrams.is_in_settlement === 'all') delete newPrams.is_in_settlement;
    const newPagination = {
      ...pagination,
      page: 1,
    };

    Log.info("Search Payout Reconciled list", newPrams);
    setFilter(newPrams);
    queryList(newPagination, newPrams);
  };

  // ------------------ end of filter stuffies --------------------//

  useEffect(() => {
    onFilterSubmit(defaultValue);
  }, []);

  const isUserNotFound = tableData?.results?.length === 0;

  const filteredList = applySortFilter(
    tableData?.results || [],
    order,
    orderBy
  );

  const schema = [
    {
      type: "daterange",
      label: "Transaction Time",
      param: ["start_datetime", "end_datetime"],
      width: 6
    },
    {
      type: "date",
      label: "Batch Date",
      param: "batch_date",
    },
    {
      type: "text",
      label: "Transaction ID",
      param: "transaction_id",
    },
    {
      type: "text",
      label: "Terminal ID",
      param: "terminal_id",
    },
    {
      type: "text",
      label: "Merchant Name",
      param: "merchant_name",
    },
    {
      type: "search-select",
      label: "Acquirer",
      param: "acquirer",
      options: acquirerList.map(item => ({
        id: item.id,
        label: item.name
      }))
    },
    {
      type: "select",
      label: "Settlement",
      param: "is_in_settlement",
      options: [
        { name: "All", id: "all" },
        { name: "Yes", id: true },
        { name: "No", id: false },
      ]
    }
  ];

  return (
    <Box>
      <FormSearch
        schema={schema}
        loading={true}
        onSearch={onFilterSubmit}
        defaultValue={defaultValue}
      />
      {loaded ? (
        <Card>
          <Scrollbar>
            <TableContainer>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  headLabel={TABLE_HEAD}
                  checkbox={false}
                />
                <TableBody>
                  {filteredList?.map((row, i) => {
                    const {
                      transaction_id,
                      transaction_datetime,
                      terminal_id,
                      merchant_id,
                      merchant_name,
                      base_amount,
                      base_currency,
                      payment_brand,
                      acquirer,
                      batch_date,
                      is_in_settlement
                    } = row;

                    return (
                      <StyledTableRow hover key={i}>
                        <CustomTableCell field={transaction_id} />
                        <CustomTableCell field={transaction_datetime} />
                        <CustomTableCell field={terminal_id} />
                        <CustomTableCell field={merchant_id} />
                        <CustomTableCell field={merchant_name} />
                        <CustomTableCell field={base_amount} />
                        <CustomTableCell field={base_currency} />
                        <CustomTableCell field={payment_brand} />
                        <CustomTableCell field={acquirer} />
                        <CustomTableCell field={batch_date} />
                        <CustomTableCell 
                          field={
                            <Label color={is_in_settlement ? "success" : "error"}>
                              {is_in_settlement ? "YES" : "NO"}
                            </Label>
                          } 
                        />
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
                {isUserNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={9} sx={{ py: 3 }}>
                        <Typography
                          gutterBottom
                          align="center"
                          variant="subtitle1"
                        >
                          Not found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          {!isEmpty(tableData?.results) && (
            <Pagination
              count={tableData.count}
              pagination={pagination}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Card>
      ) : (
        <CircularLoader />
      )}
    </Box>
  );
}
